import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CalendarPlus, CalendarCheck2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Field, TextInput, SelectInput } from "@/components/Field";
import AdminHint from "@/components/admin/AdminHint";
import { TIME_SLOTS } from "@/data/content";

const today = new Date().toISOString().split("T")[0];

function fmt(date) {
  try {
    return new Date(date + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
    });
  } catch { return date; }
}

export default function AvailabilityPanel() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [timeSlot, setTimeSlot] = useState(TIME_SLOTS[0]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/slots");
      setSlots(data || []);
    } catch {
      toast.error("Could not load availability.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addSlot = async () => {
    if (!date) { toast.error("Please choose a date."); return; }
    setBusy(true);
    try {
      await api.post("/admin/slots", { date, time_slot: timeSlot });
      toast.success("Session time added.");
      setDate("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add slot.");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/slots/${id}`);
      toast.success("Removed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove.");
    }
  };

  const openCount = slots.filter((s) => s.status === "open").length;

  return (
    <div>
      <AdminHint>
        Add the dates and times you’re available for sessions. Visitors can only request the times you add here, and a
        time disappears the moment someone books it — so you’ll never be double-booked. Declining a booking frees its time again.
      </AdminHint>
      <div className="grid lg:grid-cols-[320px,1fr] gap-8">
      {/* Add form */}
      <div className="rounded-3xl bg-white border border-brand-line p-6 md:p-7 self-start" data-testid="availability-add">
        <div className="flex items-center gap-2 mb-5">
          <CalendarPlus className="text-brand-forest" size={20} strokeWidth={1.5} />
          <h2 className="font-heading text-2xl text-brand-ink">Add a session time</h2>
        </div>
        <div className="space-y-5">
          <Field label="Date" required>
            <TextInput type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} data-testid="slot-date" />
          </Field>
          <Field label="Time" required>
            <SelectInput value={timeSlot} onChange={(e) => setTimeSlot(e.target.value)} data-testid="slot-time">
              {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
            </SelectInput>
          </Field>
          <button
            onClick={addSlot}
            disabled={busy}
            data-testid="add-slot-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-forest text-white px-4 py-3 font-body text-sm hover:bg-brand-forest-dark disabled:opacity-60"
          >
            <Plus size={16} strokeWidth={1.5} /> Add session time
          </button>
          <p className="font-body text-xs text-brand-slate/70">
            Clients can only request sessions from the open times you add here. A time disappears from the site as soon as it's requested.
          </p>
        </div>
      </div>

      {/* Slots list */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl text-brand-ink">Your session times</h3>
          <span className="font-body text-sm text-brand-slate">{openCount} open</span>
        </div>
        {loading ? (
          <p className="font-body text-brand-slate">Loading…</p>
        ) : slots.length === 0 ? (
          <div className="rounded-3xl bg-white border border-brand-line p-10 text-center">
            <p className="font-body text-brand-slate">No session times yet. Add your first one on the left so clients can book.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="slots-list">
            {slots.map((s) => (
              <div key={s.id} className="rounded-2xl bg-white border border-brand-line p-4 md:p-5 flex items-center justify-between gap-4" data-testid={`slot-${s.id}`}>
                <div className="flex items-center gap-4">
                  <span className={`h-10 w-10 rounded-full flex items-center justify-center ${s.status === "open" ? "bg-brand-lavender-soft" : "bg-brand-accent"}`}>
                    <CalendarCheck2 className={s.status === "open" ? "text-brand-lavender-dark" : "text-brand-slate"} size={18} strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="font-body text-brand-ink">{fmt(s.date)}</p>
                    <p className="font-body text-sm text-brand-slate">{s.time_slot}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-3 py-1 text-xs font-body capitalize ${s.status === "open" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {s.status === "open" ? "Open" : "Booked"}
                  </span>
                  <button onClick={() => del(s.id)} data-testid={`delete-slot-${s.id}`} className="text-rose-500 hover:bg-rose-50 rounded-lg p-2">
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
